import { ColDef } from 'ag-grid-community';
import { memoize } from 'lodash';
import { FieldOptionBase } from 'api/tasks';
import { Comparator } from 'components/atoms/grid';
import { toRowNodeComparator } from 'components/atoms/grid/plugins/with-columns';
import {
  FilterableField,
  MultiSelectField,
  SingleSelectField,
} from 'components/organisms/tasks/fields';
import { Field } from 'stores/fields';
import { getBaseComparator } from 'utils/comparators/base-comparator';
import { byNumber } from 'utils/comparators/by-number';
import { sortByText } from 'utils/sort-by-text';
import { ProjectFieldsName } from '../../fields';

type OptionsField = SingleSelectField | MultiSelectField;
type Nullable<T> = T | null;

const getProjectComparator = <T = any>(
  field: Pick<Field, 'type' | 'metaData' | 'fieldName'>,
): Comparator<T> | undefined => {
  const options =
    (field as OptionsField).metaData?.options ?? ([] as FieldOptionBase[]);
  const getLabel = memoize(
    (value: Nullable<number>) =>
      value == null ? null : options.find((o) => o.value === value)?.label,
    (value) => value?.toString() ?? 'null',
  );

  if (field.fieldName === ProjectFieldsName.projectHours) {
    const getValue = (record) => record?.total_minutes ?? null;

    return (a: T, b: T) => {
      const a_value = getValue(a as any);
      const b_value = getValue(b as any);

      return byNumber(a_value, b_value);
    };
  }

  if (field.fieldName === 'currentPhases') {
    const getLengthComparator = (a, b) => {
      if (a.length === b.length) {
        return 0;
      }

      return a.length > b.length ? 1 : -1;
    };

    const compareByArrayValue = (a, b) => {
      const aVal = a.join(',');
      const bVal = b.join(',');

      if (aVal === bVal) {
        return 0;
      }

      return aVal > bVal ? 1 : -1;
    };

    const getVal = memoize(
      (value: Nullable<number[]>): string[] =>
        (value ?? []).map((x) => getLabel(x)).filter((x) => x) as string[],
      (value: Nullable<number[]>) =>
        Array.from(new Set(value ?? []))
          .map((x) => x.toString())
          .join(','),
    );

    return (first: T, sec: T) => {
      const a = getVal(first as any) ?? 'null';
      const b = getVal(sec as any) ?? 'null';

      if (a.length !== b.length) {
        return getLengthComparator(a, b);
      }

      return compareByArrayValue(a, b);
    };
  }

  if (field.fieldName === ProjectFieldsName.projectName) {
    return (a: T, b: T) =>
      sortByText(a as unknown as string, b as unknown as string);
  }

  return getBaseComparator(field);
};

export const getComparator = (field: FilterableField): ColDef['comparator'] => {
  return toRowNodeComparator(getProjectComparator(field));
};
